function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Typography } from 'seagull';
import { AnchorLogo } from 'seagull/icons';
import { redirect, routes } from "../../constants";
import { useEventReporter, useOverlayContext, useStoreActions, useStoreStatic } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import styles from "./Notification.module.scss";
import { notificationRouter } from "./notificationRouter";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const acceptNotificationLoader = 'acceptNotification';
const invalidInvitationError = 'invalidInvitation';
function InvalidInvitationIndicator() {
  return /*#__PURE__*/_jsx("div", {
    className: styles.invalidWrapper,
    "data-testid": "invalid-invitation",
    children: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(AnchorLogo, {
        className: styles.logo
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h3",
        weight: "normal",
        children: "This invitation link is no longer valid"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        children: "If you would like to join the account, we recommend contacting the person who invited you."
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        children: "For support, please email support@sayanchor.com."
      })]
    })
  });
}
export default function Notification(props) {
  const {
    notificationId
  } = props.match.params;
  const [error, setError] = useState();
  const {
    reportToHeap,
    reportToGtm,
    reportToAppcues,
    reportToHubspot
  } = useEventReporter();
  const {
    getState
  } = useStoreStatic();
  const {
    api,
    setAuthToken
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const location = useLocation();
  const history = useHistory();
  const featureFlags = useFeatureFlags();
  useOnMount(() => {
    async function handleAuthenticationAndRedirect() {
      const isLoggedIn = !!getState().authToken;
      overlayContext.startLoader(acceptNotificationLoader);
      let redirectToUrl, stateChanges;
      try {
        const searchParams = new URLSearchParams(location.search);
        const intent = searchParams.get('intent') || undefined;
        const notification = await (isLoggedIn ? api.notification.getWhileLoggedIn(notificationId, intent) : api.notification.getWhileLoggedOut(notificationId, intent));
        if (notification.authToken != null) await setAuthToken(notification.authToken);
        if (notification && notification.notificationType) {
          reportToGtm('notification-open', notification.notificationType);
          reportToAppcues('notification-open', notification.notificationType);
          reportToHeap('notification', 'open', notification.notificationType);
        }
        const {
          redirect,
          error,
          hubspotEventName
        } = notificationRouter({
          notification,
          claims: getState().claims,
          userInfo: getState().userInfo,
          featureFlags
        });
        hubspotEventName && reportToHubspot(hubspotEventName);
        redirectToUrl = redirect.url;
        stateChanges = redirect.state;
        if (error) {
          const err = new Error(error + " - type:".concat(notification.type || '(empty)', ";notificationType:").concat(notification.notificationType || '(empty)'));
          Sentry.withScope(scope => {
            scope.setContext('Notification data', {
              notificationPayload: JSON.stringify(notification)
            });
            Sentry.captureException(err);
          });
          overlayContext.showErrorToast("Oops! We couldn't find the correct page. Please contact support@sayanchor.com and let us know.", 'notification-redirect-toast');
        }
      } catch (err) {
        const e = new ErrorResponse(err);
        if (e.status === 401) {
          if (isLoggedIn) {
            setAuthToken(null);
          }
          const loginFlow = e.getLoginFlowData();
          const recipientEmail = e.getRecipientEmail();
          if (loginFlow || recipientEmail) {
            stateChanges = _objectSpread(_objectSpread({}, loginFlow && {
              setLoginFlow: loginFlow
            }), recipientEmail && {
              recipientEmail: recipientEmail
            });
          }
          redirectToUrl = redirect.getPathAndSearch({
            returnTo: location.pathname
          });
        } else if (e.status === 403) {
          const recipientEmail = e.getRecipientEmail();
          const sourceUrl = location.pathname;
          const notificationNoAccessState = {
            recipientEmail: recipientEmail,
            returnTo: sourceUrl
          };
          stateChanges = notificationNoAccessState;
          redirectToUrl = routes.noAccessPage;
        } else if (e.hasServerError(ServerError.RecipientNotFound) && isLoggedIn) {
          // logout flow will trigger an unmount/mount cycle because this component renders only when authFlowDone is true.
          await setAuthToken(null);
        } else if (e.hasServerError(ServerError.InvalidTeamInvitation)) {
          setError(invalidInvitationError);
        } else {
          redirectToUrl = routes.home;
        }
      }
      if (redirectToUrl) {
        history.replace(redirectToUrl, stateChanges);
      }
      overlayContext.stopLoader(acceptNotificationLoader);
    }
    handleAuthenticationAndRedirect();
  });
  return error === invalidInvitationError ? /*#__PURE__*/_jsx(InvalidInvitationIndicator, {}) : null;
}